import { useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isoDateTime, paisa, type Money } from '@fleet/shared';
import {
  useBookingQuery,
  useDeleteBookingMutation,
  useInvoicesQuery,
  usePaymentsQuery,
  useTransitionBookingMutation,
  useUpdateBookingMutation,
} from '@/api/endpoints';
import type { Invoice } from '@/api/types';
import { useCan } from '@/app/session';
import { Button, LinkButton } from '@/design/Button';
import { Identifier, MoneyText } from '@/design/Data';
import { ErrorState } from '@/design/EmptyState';
import { Confirm } from '@/design/Confirm';
import { Field, FieldRow, MoneyInput, TextInput } from '@/design/Field';
import { Panel, PanelBody } from '@/design/Panel';
import { InvoiceModal } from '@/features/money/InvoiceModal';
import { RaiseInvoiceModal } from '@/features/money/RaiseInvoiceModal';
import { PaymentModal } from '@/features/money/PaymentModal';
import { BookingChargesPanel } from './BookingChargesPanel';
import { StatusPill } from '@/design/StatusPill';
import { useToast } from '@/design/Toast';
import { readApiError } from '@/lib/apiError';
import { useTime } from '@/lib/time';
import { bookingStatus, TRANSITION_VERBS } from './status';

/**
 * One booking, and what happens to it next.
 *
 * The transition buttons are the screen's reason to exist. They come from the
 * server — only moves the API would actually allow are drawn, so nothing here
 * can be pressed to produce an error.
 */
export function BookingDetailPage(): ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: booking, isLoading, isError, refetch } = useBookingQuery(id);
  const [transition, transitioning] = useTransitionBookingMutation();
  const canCancel = useCan('bookings.cancel');
  const { formatDateTime } = useTime();
  const [reason, setReason] = useState('');
  const [asking, setAsking] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);

  const canTakePayment = useCan('bookings.record_payment');
  const toast = useToast();
  const paid = booking?.paid ?? 0;
  const owed = booking?.owed ?? 0;

  /**
   * The bill, on the screen the booking is on.
   *
   * QA asked, in as many words, "how do we create an invoice?" — and the
   * honest answer was that you could not, unless the booking happened to be
   * on the Dues list. Dues only holds trips that have *happened and still owe
   * money*, so a customer who paid in full on the day — the one most likely to
   * ask for a bill — was the one customer there was no way to invoice.
   */
  const invoices = useInvoicesQuery({ booking: id });
  const [showingInvoice, setShowingInvoice] = useState<Invoice | null>(null);
  const raised = invoices.data?.results.find((candidate) => candidate.status !== 'void');

  /**
   * The payments themselves, not just their sum.
   *
   * "Paid: Rs 6,000" answers how much and not when, how, or by whom — which
   * are the three things asked when somebody rings to query a deposit they
   * took a fortnight ago.
   */
  const payments = usePaymentsQuery({ booking: id });

  /**
   * Editing the price here rather than on the booking form.
   *
   * The price is what changes after a booking is taken — a rate agreed down
   * on the phone, a discount for a repeat customer — and going back through
   * the whole form to change one number means re-validating dates, vehicle
   * and licence to edit a figure. The server still refuses once an invoice
   * exists; this just puts the two fields where the money already is.
   */
  const [editingMoney, setEditingMoney] = useState(false);
  const [subtotal, setSubtotal] = useState<Money>(paisa(0));
  const [discount, setDiscount] = useState<Money>(paisa(0));
  const [updateBooking, savingMoney] = useUpdateBookingMutation();

  const [deleteBooking, deleting] = useDeleteBookingMutation();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const problems = readApiError(transitioning.error ?? savingMoney.error);

  function startEditing(): void {
    setSubtotal(paisa(booking?.subtotal ?? 0));
    setDiscount(paisa(booking?.discount ?? 0));
    setEditingMoney(true);
  }

  async function saveMoney(): Promise<void> {
    try {
      await updateBooking({
        id,
        // `total` is sent because the booking serializer still takes it; the
        // charges below are already inside what the server recalculates.
        body: { subtotal, discount, total: paisa(Math.max(0, subtotal - discount)) },
      }).unwrap();
      setEditingMoney(false);
      toast.done('Price updated.');
    } catch {
      // The refusal — usually "this has been invoiced" — renders below.
    }
  }

  async function remove(): Promise<void> {
    try {
      await deleteBooking(id).unwrap();
      toast.done(`${booking?.reference ?? 'The booking'} deleted.`);
      navigate('/bookings');
    } catch {
      setConfirmingDelete(false);
      toast.problem(readApiError(deleting.error).detail ?? 'That booking was not deleted.');
    }
  }

  /**
   * Raising goes through a modal now, to ask for the customer's tax number.
   *
   * It has to be asked *before* the number is allocated: an issued invoice is
   * never edited here, so adding a PAN afterwards means voiding the document
   * and raising another.
   */
  const [raisingFor, setRaisingFor] = useState<{ id: string; reference: string } | null>(null);

  if (isError) {
    return (
      <ErrorState
        headline="This booking did not load"
        detail="Go back to the bookings list and open it again."
        onRetry={() => void refetch()}
        retryLabel="Load again"
      />
    );
  }

  if (isLoading || booking === undefined) {
    return <div className="h-40" aria-busy="true" />;
  }

  const needsReason = (target: string): boolean =>
    target === 'cancelled' || target === 'no_show';

  async function move(target: string): Promise<void> {
    if (needsReason(target) && asking !== target) {
      setAsking(target);
      return;
    }
    await transition({ id, to: target, reason }).unwrap();
    setAsking(null);
    setReason('');
  }

  const facts = [
    { label: 'Customer', value: booking.customer_name },
    { label: 'Phone', value: booking.customer_phone, identifier: true },
    { label: 'Vehicle', value: booking.vehicle_name ?? booking.category ?? '—' },
    { label: 'Driver', value: booking.driver_name ?? (booking.with_driver === true ? 'Not assigned' : 'Self-drive') },
    // On a self-drive booking this is the thing somebody checks against the
    // licence in their hand at handover, so it belongs on the face of it.
    ...(booking.with_driver === true
      ? []
      : [{ label: 'Their licence', value: booking.customer_licence_no || '—', identifier: true }]),
    { label: 'Out at', value: formatDateTime(isoDateTime(booking.start_at)) },
    { label: 'Back at', value: formatDateTime(isoDateTime(booking.end_at)) },
    { label: 'Area', value: booking.area_label ?? '—' },
    { label: 'Taking it to', value: booking.destination || '—' },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-display font-semibold">
            <Identifier>{booking.reference}</Identifier>
          </h1>
          <StatusPill status={bookingStatus(booking.status)} />
        </div>
        <LinkButton to={`/bookings/${id}/edit`}>Edit booking</LinkButton>
      </div>

      <Panel title="What happens next" note="Only the moves this booking can actually make" current>
        <PanelBody>
          {(booking.next_statuses ?? []).length === 0 ? (
            <p className="text-ink-muted">
              This booking is {bookingStatus(booking.status).label.toLowerCase()} and
              does not change again.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2">
                {(booking.next_statuses ?? []).map((option) => {
                  // The schema types these loosely because they come from a
                  // method field; the server only ever sends both.
                  const value = String(option.value ?? '');
                  const blocked = needsReason(value) && !canCancel;
                  return (
                    <Button
                      key={value}
                      size="touch"
                      intent={
                        needsReason(value)
                          ? 'danger'
                          : value === 'on_trip' || value === 'returned'
                            ? 'primary'
                            : 'secondary'
                      }
                      disabled={blocked || transitioning.isLoading}
                      title={blocked ? 'A manager has to do this' : undefined}
                      onClick={() => void move(value)}
                    >
                      {TRANSITION_VERBS[value] ?? option.label}
                    </Button>
                  );
                })}
              </div>

              {asking !== null && (
                <div className="border-l-[3px] border-l-alert bg-alert-wash px-3 py-3">
                  <Field
                    label={`Why are you ${asking === 'cancelled' ? 'cancelling' : 'marking a no-show'}?`}
                    required
                  >
                    {({ id: fieldId, describedBy }) => (
                      <TextInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={reason}
                        onChange={setReason}
                        autoFocus
                      />
                    )}
                  </Field>
                  <div className="mt-3 flex gap-2">
                    <Button
                      intent="danger"
                      disabled={reason.trim() === '' || transitioning.isLoading}
                      onClick={() => void move(asking)}
                    >
                      {TRANSITION_VERBS[asking] ?? 'Confirm'}
                    </Button>
                    <Button intent="quiet" onClick={() => setAsking(null)}>
                      Keep the booking
                    </Button>
                  </div>
                </div>
              )}

              {!canCancel && (
                <p className="text-small text-ink-muted">
                  Cancelling is a manager's decision, so those buttons are turned off for this
                  account.
                </p>
              )}

              {problems.detail !== undefined && (
                <p className="border-l-[3px] border-l-alert bg-alert-wash px-3 py-2 font-medium text-alert" role="alert">
                  {problems.detail}
                </p>
              )}
            </div>
          )}
        </PanelBody>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="The booking">
          <dl className="divide-y divide-rule">
            {facts.map((fact) => (
              <div key={fact.label} className="flex justify-between gap-4 px-4 py-2">
                <dt className="text-ink-muted">{fact.label}</dt>
                <dd className={fact.identifier === true ? 'identifier text-right' : 'text-right'}>
                  {fact.value}
                </dd>
            </div>
            ))}
          </dl>
        </Panel>

        {/*
          What they owe, on the screen somebody opens when the customer rings
          to ask. This panel used to say "Payments are the next step" and show
          only the quote — so the one question being asked could not be
          answered here, and staff had to go to Dues and find the booking
          again. Dues only lists trips that have *happened*, so for a booking
          still to come with a deposit against it there was nowhere at all.
        */}
        <Panel
          title="The money"
          note={owed > 0 ? 'Not settled' : paid > 0 ? 'Settled' : 'Nothing taken yet'}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {!editingMoney && raised === undefined && (
                <Button onClick={startEditing}>Edit price</Button>
              )}
              {canTakePayment && owed > 0 && (
                <Button intent="primary" onClick={() => setPaying(true)}>
                  Take payment
                </Button>
              )}
              {raised !== undefined ? (
                <Button onClick={() => setShowingInvoice(raised)}>
                  Invoice {raised.number}
                </Button>
              ) : (
                booking.no_bill !== true && (
                  <Button
                    onClick={() =>
                      setRaisingFor({ id: booking.id, reference: booking.reference })
                    }
                  >
                    Raise an invoice
                  </Button>
                )
              )}
            </div>
          }
        >
          {editingMoney ? (
            <PanelBody>
              <form
                className="flex flex-col gap-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  void saveMoney();
                }}
              >
                <FieldRow>
                  <Field label="Subtotal" error={problems.fields['subtotal']}>
                    {({ id: fieldId, describedBy }) => (
                      <MoneyInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={subtotal}
                        onChange={setSubtotal}
                      />
                    )}
                  </Field>
                  <Field label="Discount" error={problems.fields['discount']}>
                    {({ id: fieldId, describedBy }) => (
                      <MoneyInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={discount}
                        onChange={setDiscount}
                      />
                    )}
                  </Field>
                </FieldRow>

                {problems.detail !== undefined && (
                  <p
                    className="border-l-[3px] border-l-alert bg-alert-wash px-3 py-2 font-medium text-alert"
                    role="alert"
                  >
                    {problems.detail}
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button intent="primary" type="submit" disabled={savingMoney.isLoading}>
                    {savingMoney.isLoading ? 'Saving' : 'Save price'}
                  </Button>
                  <Button onClick={() => setEditingMoney(false)}>Cancel</Button>
                </div>
              </form>
            </PanelBody>
          ) : null}

          <dl className="divide-y divide-rule">
            <div className="flex justify-between px-4 py-2">
              <dt className="text-ink-muted">Subtotal</dt>
              <dd><MoneyText amount={paisa(booking.subtotal ?? 0)} /></dd>
            </div>
            <div className="flex justify-between px-4 py-2">
              <dt className="text-ink-muted">Discount</dt>
              <dd><MoneyText amount={paisa(booking.discount ?? 0)} /></dd>
            </div>
            <div className="flex justify-between px-4 py-2">
              <dt className="font-medium">Total</dt>
              <dd className="font-semibold"><MoneyText amount={paisa(booking.total ?? 0)} /></dd>
            </div>
            <div className="flex justify-between px-4 py-2">
              <dt className="text-ink-muted">Advance Received</dt>
              <dd>
                {paid === 0 ? (
                  <span className="text-ink-faint">—</span>
                ) : (
                  <MoneyText amount={paisa(paid)} />
                )}
              </dd>
            </div>
            <div className="flex justify-between px-4 py-2.5">
              <dt className="font-medium">Due</dt>
              <dd
                className={`text-heading font-semibold ${owed > 0 ? 'text-attention' : 'text-ink'}`}
              >
                <MoneyText amount={paisa(Math.max(owed, 0))} />
              </dd>
            </div>
          </dl>

          {/* Every payment, with when and how. The sum above answers "how
              much"; this answers the questions actually asked when somebody
              rings about a deposit they handed over a fortnight ago. */}
          {(payments.data?.results.length ?? 0) > 0 && (
            <ul className="divide-y divide-rule border-t border-rule">
              {(payments.data?.results ?? []).map((payment) => (
                <li key={payment.id} className="flex items-baseline gap-3 px-4 py-2 text-small">
                  <span className="text-ink-muted">
                    {formatDateTime(isoDateTime(payment.received_at))}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {payment.kind_label} · {payment.method_label}
                    {payment.received_by_name !== null &&
                      payment.received_by_name !== undefined && (
                        <span className="text-ink-muted"> · {payment.received_by_name}</span>
                      )}
                  </span>
                  <span
                    className={
                      payment.is_void === true
                        ? 'tabular-nums text-ink-faint line-through'
                        : 'tabular-nums font-medium'
                    }
                  >
                    <MoneyText amount={paisa(payment.amount)} />
                  </span>
                </li>
              ))}
            </ul>
          )}

          {booking.no_bill === true && (
            <PanelBody>
              <p className="border-l-[3px] border-l-attention bg-attention-wash px-3 py-2 text-small">
                Sold without a bill — the VAT has been taken out of the total, and no invoice can
                be raised against it.
              </p>
            </PanelBody>
          )}
          {booking.notes !== undefined && booking.notes !== '' && (
            <PanelBody>
              <p className="text-ink-muted">{booking.notes}</p>
            </PanelBody>
          )}
        </Panel>
      </div>

      <BookingChargesPanel
        bookingId={id}
        reference={booking.reference}
        locked={raised !== undefined}
      />

      {/* Pushed to the bottom, away from everything anybody presses daily. */}
      {canCancel && (
        <div className="flex flex-wrap items-center gap-3 border-t border-rule pt-4">
          <Button
            intent="danger"
            size="touch"
            disabled={deleting.isLoading}
            onClick={() => setConfirmingDelete(true)}
          >
            Delete this booking
          </Button>
          <span className="text-small text-ink-muted">
            Removes it outright, with its payments and invoices. Cancelling keeps the record.
          </span>
        </div>
      )}

      <Confirm
        open={confirmingDelete}
        title={`Delete ${booking.reference}?`}
        detail={
          <>
            <p>
              The booking goes, and so does every payment and invoice against it. This cannot be
              undone.
            </p>
            <p className="mt-2 text-ink-muted">
              An issued invoice number is part of a run an accountant checks, and deleting one
              leaves a gap in it. If this trip happened and then did not,{' '}
              <span className="font-medium">cancel</span> it instead — that keeps the record and
              the numbers.
            </p>
          </>
        }
        confirmLabel="Delete it"
        tone="alert"
        busy={deleting.isLoading}
        onConfirm={() => void remove()}
        onCancel={() => setConfirmingDelete(false)}
      />

      <PaymentModal
        booking={
          paying && booking !== undefined
            ? {
                id: booking.id,
                reference: booking.reference,
                customerName: booking.customer_name,
                owed,
                // Before the trip, money taken is an advance rather than
                // settlement — the Dues screen makes the same distinction.
                isAdvance: booking.status === 'confirmed',
              }
            : null
        }
        onClose={() => setPaying(false)}
      />

      <RaiseInvoiceModal
        booking={raisingFor}
        customerPan={booking.customer_pan ?? ''}
        onRaised={(invoice) => {
          setRaisingFor(null);
          setShowingInvoice(invoice);
        }}
        onClose={() => setRaisingFor(null)}
      />

      <InvoiceModal invoice={showingInvoice} onClose={() => setShowingInvoice(null)} />
    </div>
  );
}
